import { APIConfig } from 'src/config';

/**
 * Logger utility with configurable severity levels.
 * Provides wrappers over console methods.
 */

// Severity levels with numeric priorities
const LOG_SEVERITY_LEVELS = {
  TRACE: 0,
  DEBUG: 1,
  LOG: 2,
  INFO: 3,
  WARN: 4,
  ERROR: 5,
};

// Default configuration
const DEFAULT_CONFIG = {
  SEVERITY: LOG_SEVERITY_LEVELS.INFO,
  ENABLED: true,
};
const CONFIG = APIConfig()?.LOGGING || DEFAULT_CONFIG;

/**
 * Configure the logger - This is for component level configuration during development.
 * For example: logger.configure({ level: 'WARN' }); - sets the severity to WARN
 * For example: logger.configure({ level: '4' }); - also sets the severity to WARN
 * @param {object} options - Configuration options
 * @param {string|number} options.level - Minimum severity level to log
 * @param {boolean} options.enabled - Whether logging is enabled
 */
const configure = ({ level, enabled } = {}) => {
  if (level) {
    if (typeof level === 'string') {
      const upperLevel = level.toUpperCase();
      if (upperLevel in LOG_SEVERITY_LEVELS) {
        CONFIG.SEVERITY = LOG_SEVERITY_LEVELS[upperLevel];
      } else if (Number.isInteger(Number(level))) {
        CONFIG.SEVERITY = Number(level);
      } else {
        console.warn(`[Logger] Unknown level string: ${level}. Log level remains at ${CONFIG.SEVERITY}`);
      }
    } else if (Object.values(LOG_SEVERITY_LEVELS).includes(Number(level))) {
      CONFIG.SEVERITY = Number(level);
    } else {
      console.warn(`[Logger] Invalid level: ${level}. Log level remains at ${CONFIG.SEVERITY}`);
    }
  }

  if (enabled !== undefined) {
    CONFIG.ENABLED = Boolean(enabled);
  }
};

/**
 * Generic method wrapper.
 * @param {number} severity
 * @param {Function} consoleMethod
 * @param {Array} args
 */
const logMethod = (severity, consoleMethod, args) => {
  if (!CONFIG.ENABLED || severity === undefined || severity === null || !consoleMethod) return;

  const shouldExecute = severity >= CONFIG.SEVERITY;
  if (shouldExecute) {
    consoleMethod(...args);
  }
};

// Core log methods  (Execute based severity if config is enabled)
const trace = (...args) => logMethod(LOG_SEVERITY_LEVELS.TRACE, console.trace, args);
const debug = (...args) => logMethod(LOG_SEVERITY_LEVELS.DEBUG, console.debug, args);
const log = (...args) => logMethod(LOG_SEVERITY_LEVELS.LOG, console.log, args);
const info = (...args) => logMethod(LOG_SEVERITY_LEVELS.INFO, console.info, args);
const warn = (...args) => logMethod(LOG_SEVERITY_LEVELS.WARN, console.warn, args);
const error = (...args) => logMethod(LOG_SEVERITY_LEVELS.ERROR, console.error, args);

// Utility methods (Execute if config is enabled)
const group = (...args) => CONFIG.ENABLED && console.group(...args);
const groupCollapsed = (...args) => CONFIG.ENABLED && console.groupCollapsed(...args);
const groupEnd = () => CONFIG.ENABLED && console.groupEnd();
const time = (label) => CONFIG.ENABLED && console.time(label);
const timeLog = (label) => CONFIG.ENABLED && console.timeLog(label);
const timeEnd = (label) => CONFIG.ENABLED && console.timeEnd(label);
const table = (...args) => CONFIG.ENABLED && console.table(...args);
const count = (label) => CONFIG.ENABLED && console.count(label);
const countReset = (label) => CONFIG.ENABLED && console.countReset(label);
const clear = () => CONFIG.ENABLED && console.clear();
const assert = (cond, ...args) => CONFIG.ENABLED && console.assert(cond, ...args);
const dir = (obj) => CONFIG.ENABLED && console.dir(obj);
const dirxml = (...args) => CONFIG.ENABLED && console.dirxml(...args);

export default {
  configure,
  LOG_SEVERITY_LEVELS,

  // Severity methods
  trace,
  debug,
  log,
  info,
  warn,
  error,

  // Utility methods
  group,
  groupCollapsed,
  groupEnd,
  time,
  timeLog,
  timeEnd,
  table,
  count,
  countReset,
  clear,
  assert,
  dir,
  dirxml,
};
